// Wave 20.27 — Unified Drop Link picker.
//
// One dialog for every kind of drop the app can mint (activity,
// peak, session-total stat). Parent supplies:
//   - a description string ("Drop Link" or "Drop Peak Power · 3:20")
//   - the onSubmit callback which does the actual API call and
//     receives {window, visibility, recipients}
//
// The dialog owns the UI + local state (window / visibility /
// recipients). It re-uses UserPicker for recipients so the search
// affordance is identical to the mobile PostActivity flow.

import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Select,
  Stack,
  Typography,
} from '@mui/material';
import UserPicker, { UserOption } from './UserPicker';
import { colors } from '../theme';

export type ShareWindow = '30m' | '1h' | '6h' | '24h' | '7d' | 'forever';
export type Visibility = 'public' | 'followers' | 'private';

export interface DropSubmit {
  window: ShareWindow;
  visibility: Visibility;
  recipients: UserOption[];
}

interface Props {
  open: boolean;
  onClose: () => void;
  onSubmit: (opts: DropSubmit) => Promise<{ url: string } | void>;
  /** Small header line — 'Drop Peak Power · 3:20' or 'Drop 25 mi run'. */
  headline: string;
  /** Cap the visibility dropdown when the parent doesn't want to
   *  allow followers-only (e.g. mint from a private session). */
  disableFollowers?: boolean;
  defaultWindow?: ShareWindow;
  defaultVisibility?: Visibility;
}

const WINDOWS: Array<{ value: ShareWindow; label: string }> = [
  { value: '30m', label: '30 minutes' },
  { value: '1h', label: '1 hour' },
  { value: '6h', label: '6 hours' },
  { value: '24h', label: '24 hours' },
  { value: '7d', label: '7 days' },
  { value: 'forever', label: 'Never expires' },
];

export default function DropLinkDialog({
  open,
  onClose,
  onSubmit,
  headline,
  disableFollowers,
  defaultWindow = '24h',
  defaultVisibility = 'public',
}: Props) {
  const [window, setWindow] = useState<ShareWindow>(defaultWindow);
  const [visibility, setVisibility] = useState<Visibility>(defaultVisibility);
  const [recipients, setRecipients] = useState<UserOption[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [url, setUrl] = useState<string | null>(null);

  // Reset the local state when the dialog opens with a fresh intent.
  useEffect(() => {
    if (open) {
      setWindow(defaultWindow);
      setVisibility(defaultVisibility);
      setRecipients([]);
      setError(null);
      setUrl(null);
    }
  }, [open, defaultWindow, defaultVisibility]);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      const result = await onSubmit({ window, visibility, recipients });
      if (result && result.url) {
        setUrl(result.url);
        try {
          await navigator.clipboard.writeText(result.url);
        } catch {
          /* ignore — the URL still shows in the success panel */
        }
      } else {
        // No URL to display — assume the caller took care of the
        // success UX and close the dialog.
        onClose();
      }
    } catch (e: any) {
      setError(e?.message || 'Drop failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ fontWeight: 800 }}>
        Drop Link
        <Typography sx={{ color: colors.textMuted, fontSize: '0.85rem', mt: 0.5 }}>
          {headline}
        </Typography>
      </DialogTitle>
      <DialogContent>
        {!url ? (
          <>
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            <Typography sx={sectionLabel}>SEND IN-APP (optional)</Typography>
            <UserPicker
              selected={recipients}
              onChange={setRecipients}
              placeholder="Search friends by username or name"
            />
            <Box sx={{ my: 2, borderBottom: `1px dashed ${colors.border}` }} />
            <Typography sx={sectionLabel}>WHO CAN VIEW</Typography>
            <Select
              fullWidth
              value={visibility}
              onChange={(e) => setVisibility(e.target.value as Visibility)}
              sx={{ mb: 2 }}
            >
              <MenuItem value="public">Anyone · anyone with the link</MenuItem>
              <MenuItem value="followers" disabled={!!disableFollowers}>
                Friends · followers, sign-in required
              </MenuItem>
            </Select>
            <Typography sx={sectionLabel}>EXPIRES IN</Typography>
            <Select
              fullWidth
              value={window}
              onChange={(e) => setWindow(e.target.value as ShareWindow)}
            >
              {WINDOWS.map((w) => (
                <MenuItem key={w.value} value={w.value}>
                  {w.label}
                </MenuItem>
              ))}
            </Select>
          </>
        ) : (
          <Box>
            <Alert severity="success" sx={{ mb: 2 }}>
              Copied to clipboard. Paste it anywhere.
            </Alert>
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              sx={{
                p: 1.5,
                borderRadius: 1.5,
                border: `1px solid ${colors.brand}`,
                backgroundColor: `${colors.brand}11`,
              }}
            >
              <Typography
                sx={{ flex: 1, fontFamily: 'monospace', fontSize: '0.85rem', wordBreak: 'break-all' }}
              >
                {url}
              </Typography>
            </Stack>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>
          {url ? 'Done' : 'Cancel'}
        </Button>
        {!url && (
          <Button
            onClick={submit}
            disabled={busy}
            variant="contained"
            sx={{ backgroundColor: colors.brand, color: colors.textInverse, fontWeight: 700 }}
          >
            {busy ? '…' : 'Create link'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

const sectionLabel = {
  color: colors.brand,
  fontSize: '0.7rem',
  letterSpacing: '0.12em',
  fontWeight: 700,
  mb: 1,
} as const;
